import {useCallback, useState} from 'react';
import {graphql, forecastFetchQuery} from '../../../graphql/forecastRelay';
import type {useForecastTaskSearchQuery$data} from './__generated__/useForecastTaskSearchQuery.graphql';

const ForecastTaskSearchQueryDoc = graphql`
	query useForecastTaskSearchQuery($searchQuery: TaskSearchQueryType) {
		viewer {
			globalSearchTaskResults(first: 25, searchQuery: $searchQuery) {
				edges {
					node {
						id
						companyTaskId
						name
						project {
							name
						}
					}
				}
			}
		}
	}
`;

export interface ForecastTaskSearchResult {
	id: string;
	companyTaskId: number | null;
	name: string | null;
	projectName: string | null;
}

/**
 * Build the globalSearchTaskResults filter for a raw query. A bare or T-prefixed
 * number searches COMPANY_TASK_ID; anything else searches NAME. Exported for unit
 * testing. (ACC-526: replaces the by-number viewer.task lookup, which 500s on
 * non-numeric input because its backend endpoint binds the id as an Integer.)
 */
export function buildTaskSearchQuery(rawQuery: string) {
	const trimmed = rawQuery.trim();
	const numberMatch = /^[Tt]?(\d+)$/.exec(trimmed);
	if (numberMatch) {
		return {filters: [{field: 'COMPANY_TASK_ID', operator: 'CONTAINS', value: [numberMatch[1]]}]};
	}
	return {filters: [{field: 'NAME', operator: 'CONTAINS', value: [trimmed]}]};
}

export function useForecastTaskSearch() {
	const [results, setResults] = useState<ForecastTaskSearchResult[]>([]);
	const [loading, setLoading] = useState(false);

	// Stable reference: consumers (e.g. AddForecastTaskModal) put `search` in an
	// effect dependency array. Recreating it every render would re-trigger that
	// effect after each setState below, re-scheduling the debounced search in a
	// perpetual loop (ACC-526). It only closes over stable state setters.
	const search = useCallback((rawQuery: string): Promise<ForecastTaskSearchResult[]> => {
		if (!rawQuery.trim()) {
			setResults([]);
			return Promise.resolve([]);
		}
		setLoading(true);
		return forecastFetchQuery(ForecastTaskSearchQueryDoc, {searchQuery: buildTaskSearchQuery(rawQuery)})
			.then(data => {
				const typed = data as useForecastTaskSearchQuery$data | null;
				const edges = typed?.viewer?.globalSearchTaskResults?.edges ?? [];
				const mapped: ForecastTaskSearchResult[] = edges.flatMap(edge =>
					edge?.node
						? [
								{
									id: edge.node.id,
									companyTaskId: edge.node.companyTaskId ?? null,
									name: edge.node.name ?? null,
									projectName: edge.node.project?.name ?? null,
								},
						  ]
						: []
				);
				setResults(mapped);
				return mapped;
			})
			.finally(() => setLoading(false));
	}, []);

	return {search, results, loading};
}
